/**
 * Alertas: o que o sistema viu e a pessoa ainda não.
 *
 * Um alerta só existe enquanto a condição existe. Resolver à mão é para quando
 * a pessoa tratou o assunto fora do sistema; o resto some sozinho na varredura
 * (`varrerAlertas`). Alerta que se acumula é alerta que ninguém lê.
 */

import { formato, type Alerta, type TipoAlerta } from '@pcpgbu/dominio';
import { botao, cabecalhoDePagina, h, render, sf, tituloDeSecao, vazio } from '@pcpgbu/ui';
import type { Aplicacao } from '../estado.js';

const APARENCIA: Record<TipoAlerta, { icone: string; cor: string; titulo: string }> = {
  os_atrasada: { icone: 'exclamationmark-triangle-fill', cor: 'var(--sys-red)', titulo: 'OS atrasada' },
  os_em_risco: { icone: 'hourglass', cor: 'var(--sys-orange)', titulo: 'OS em risco' },
  etapa_acima_do_previsto: { icone: 'timer', cor: 'var(--sys-orange)', titulo: 'Etapa demorando' },
  etapa_parada: { icone: 'pause-fill', cor: 'var(--sys-orange)', titulo: 'Etapa parada' },
  os_alterada_em_producao: { icone: 'square-and-pencil', cor: 'var(--sys-orange)', titulo: 'OS alterada em produção' },
  estacao_a_classificar: { icone: 'questionmark-circle', cor: 'var(--sys-purple)', titulo: 'Equipamento sem estação' },
  importacao_quarentena: { icone: 'tray', cor: 'var(--sys-orange)', titulo: 'PDF em quarentena' },
  divergencia_erp: { icone: 'arrow-triangle-branch', cor: 'var(--sys-blue)', titulo: 'Divergência com o sistema da gráfica' },
  dispositivo_sem_sincronizar: { icone: 'laptopcomputer', cor: 'var(--sys-orange)', titulo: 'Aparelho sem sincronizar' },
  agente_sem_enviar: { icone: 'bolt', cor: 'var(--sys-red)', titulo: 'Agente sem enviar OS' },
  evento_recusado: { icone: 'xmark-circle-fill', cor: 'var(--sys-red)', titulo: 'Apontamento recusado' },
  etapa_no_lugar_errado: { icone: 'arrow-triangle-branch', cor: 'var(--sys-purple)', titulo: 'Etapa na estação errada' },
  relogio_fora: { icone: 'clock', cor: 'var(--sys-orange)', titulo: 'Relógio do aparelho fora' },
};

export async function telaAlertas(app: Aplicacao, destino: HTMLElement): Promise<void> {
  const abertos = await app.repo.alertas();
  const todos = await app.repo.alertas(true);
  const resolvidos = todos.filter((a) => a.resolvidoEm !== null).slice(0, 30);

  render(destino, [
    h('.vstack.gap-4', [
      cabecalhoDePagina({
        olho: 'Produção',
        titulo: 'Alertas',
        lead: 'O que o sistema achou que alguém precisa olhar. Resolver aqui não muda a produção — só tira o aviso da lista.',
      }),

      abertos.length === 0
        ? h('.cartao', vazio(
            'checkmark-circle',
            'Nenhum alerta aberto',
            'O sistema avisa aqui quando uma OS atrasa, uma etapa passa do tempo previsto, um PDF não é lido ou um aparelho para de sincronizar.',
          ))
        : h('.vstack.gap-2', [
            tituloDeSecao(`Abertos · ${abertos.length}`),
            ...abertos.map((a) => linha(app, a, false)),
          ]),

      resolvidos.length > 0
        ? h('.vstack.gap-2', [
            tituloDeSecao(`Resolvidos recentemente · ${resolvidos.length}`),
            ...resolvidos.map((a) => linha(app, a, true)),
          ])
        : null,
    ]),
  ]);
}

function linha(app: Aplicacao, a: Alerta, resolvido: boolean): HTMLElement {
  const ap = APARENCIA[a.tipo];
  return h(
    '.cartao',
    { style: { padding: '11px 14px', opacity: resolvido ? '.6' : '1' } },
    h('.hstack.gap-3', [
      h('span.mac-settings-icon', { style: { background: ap.cor } }, sf(ap.icone)),
      h('.vstack', { style: { gap: '1px', flex: '1', minWidth: '0' } }, [
        h('.t-body-emph', ap.titulo),
        h('.t-callout.t-secondary', a.texto),
        h('.t-subhead.t-tertiary', [
          formato.dataHoraCurta(a.criadoEm),
          resolvido && a.resolvidoEm ? ` · resolvido ${formato.dataHoraCurta(a.resolvidoEm)}` : '',
        ]),
      ]),
      a.osNumero
        ? botao('Abrir OS', () => app.roteador.ir(`/os/${a.osNumero}`), { pequeno: true })
        : null,
      a.tipo === 'estacao_a_classificar'
        ? botao('Triagem', () => app.roteador.ir('/triagem'), { pequeno: true })
        : null,
      a.tipo === 'importacao_quarentena'
        ? botao('Importações', () => app.roteador.ir('/importacoes'), { pequeno: true })
        : null,
      !resolvido
        ? botao('Resolver', async () => {
            await app.repo.resolverAlerta(a.id, app.sessao.pessoaId);
            app.redesenhar();
          }, { pequeno: true, icone: 'checkmark' })
        : null,
    ]),
  );
}
